import React, { useEffect, useState } from 'react';
import api, { fmtINR, fmtDate, fmtDateTime, API } from '@/lib/api';
import { Loading, SectionTitle, PaymentBadge } from '@/components/Shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Download, Send, Save, Building2, Plus, Trash2, FileText, List as ListIcon, IndianRupee } from 'lucide-react';
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

const FIELDS = [
  { key: 'invoice_name', label: 'Invoice Display Name', full: true },
  { key: 'dealer_line', label: 'Dealer Line', full: true },
  { key: 'gstin', label: 'GSTIN' },
  { key: 'hsn', label: 'HSN Code' },
  { key: 'address', label: 'Address', full: true },
  { key: 'phone', label: 'Phone' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'email', label: 'Email', full: true },
];

const BANK_FIELDS = [
  { key: 'bank_name', label: 'Bank Name' },
  { key: 'bank_ac', label: 'Account Number' },
  { key: 'bank_ifsc', label: 'IFSC Code' },
  { key: 'bank_branch', label: 'Branch' },
];

function BankDetails() {
  const [companies, setCompanies] = useState(null);
  const [selected, setSelected] = useState('');
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => api.get('/billing-companies').then((r) => {
    setCompanies(r.data);
    if (r.data.length) {
      setSelected((prev) => prev || r.data[0].id);
    }
  });
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const co = (companies || []).find((c) => c.id === selected);
    if (co) setForm({ ...co });
  }, [selected, companies]);

  if (!companies) return <Loading />;
  const current = companies.find((c) => c.id === selected);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (form.bank_ifsc && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(String(form.bank_ifsc).toUpperCase())) {
      toast.error('Invalid IFSC code (e.g. SBIN0016739)');
      return false;
    }
    if (form.bank_ac && !/^\d{6,20}$/.test(String(form.bank_ac).replace(/\s/g, ''))) {
      toast.error('Account number must be 6–20 digits');
      return false;
    }
    return true;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {};
      [...FIELDS, ...BANK_FIELDS].forEach(({ key }) => { payload[key] = form[key] ?? ''; });
      const r = await api.put(`/billing-companies/${selected}`, payload);
      setCompanies((cs) => cs.map((c) => (c.id === selected ? r.data : c)));
      toast.success('Bank details updated. New invoices will reflect the changes.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Building2 className="h-4 w-4" />Billing Company</div>
        <Select value={selected} onValueChange={setSelected}>
          <SelectTrigger className="w-[320px]" data-testid="bank-company-select"><SelectValue placeholder="Select company" /></SelectTrigger>
          <SelectContent>
            {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {current?.updated_at && (
          <span className="text-xs text-muted-foreground ml-auto">Last updated {fmtDateTime(current.updated_at)}{current.updated_by ? ` by ${current.updated_by}` : ''}</span>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card p-5 space-y-6">
        <div>
          <h3 className="text-sm font-semibold mb-3">Company Details</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {FIELDS.map(({ key, label, full }) => (
              <div key={key} className={full ? 'sm:col-span-2' : ''}>
                <Label className="text-xs text-muted-foreground">{label}</Label>
                <Input value={form[key] || ''} onChange={(e) => set(key, e.target.value)} data-testid={`bank-field-${key}`} className="mt-1" />
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold mb-3">Bank Details (shown on invoices)</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {BANK_FIELDS.map(({ key, label }) => (
              <div key={key}>
                <Label className="text-xs text-muted-foreground">{label}</Label>
                <Input value={form[key] || ''} onChange={(e) => set(key, e.target.value)} data-testid={`bank-field-${key}`} className="mt-1" />
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="bank-save-button"><Save className="h-4 w-4 mr-1" />{saving ? 'Saving...' : 'Save Changes'}</Button>
        </div>
      </div>
    </div>
  );
}

const EMPTY_ROW = { product_id: '', item: '', hsn: '', qty: '', unit: 'L', price: '', gst: '0' };
const rowUid = () => `r-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const newRow = () => ({ ...EMPTY_ROW, _uid: rowUid() });
const UNITS = ['L', 'ml', 'kg', 'Nos'];
const GST_RATES = ['0', '5', '12', '18', '28'];
const CAT_DEFAULTS = { Diesel: { hsn: '27101930', gst: '0' }, Lubricants: { hsn: '27101980', gst: '18' } };

function QuotationsTab() {
  const [list, setList] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ billing_company_id: 'mfk', customer_id: '', customer_name: '', customer_address: '', customer_gstin: '', delivery_charge: '', delivery_gst: '0', credit_limit: '', credit_terms: 'Net 15', valid_until: '', notes: '' });
  const [rows, setRows] = useState([newRow()]);

  const load = () => api.get('/quotations').then((r) => setList(r.data));
  useEffect(() => {
    load();
    api.get('/billing-companies').then((r) => setCompanies(r.data)).catch(() => {});
    api.get('/customers?company=all').then((r) => setCustomers(r.data)).catch(() => {});
    api.get('/products').then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const addRow = () => setRows((l) => [...l, newRow()]);
  const rmRow = (i) => setRows((l) => (l.length > 1 ? l.filter((_, idx) => idx !== i) : l));
  const setRow = (i, patch) => setRows((l) => l.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const lineTotal = (r) => (Number(r.qty) || 0) * (Number(r.price) || 0);
  const subtotal = rows.reduce((s, r) => s + lineTotal(r), 0);
  const deliveryGst = (Number(form.delivery_charge) || 0) * ((Number(form.delivery_gst) || 0) / 100);
  const gstTotal = rows.reduce((s, r) => s + lineTotal(r) * ((Number(r.gst) || 0) / 100), 0) + deliveryGst;
  const grand = subtotal + gstTotal + (Number(form.delivery_charge) || 0);

  const pickProduct = (i, pid) => {
    if (pid === 'custom') { setRow(i, { product_id: 'custom', item: '', hsn: '', gst: '0' }); return; }
    const p = products.find((x) => x.id === pid);
    const d = CAT_DEFAULTS[p?.category] || { hsn: '', gst: '0' };
    setRow(i, {
      product_id: pid, item: p?.name || '', price: p?.price ?? '',
      unit: UNITS.includes(p?.unit) ? p.unit : 'L', hsn: d.hsn, gst: d.gst,
    });
  };

  const pickCustomer = (id) => {
    const c = customers.find((x) => x.id === id);
    setForm((f) => ({ ...f, customer_id: id, customer_name: c?.company_name || '', customer_address: c?.billing_address || '', customer_gstin: c?.gstin || '', credit_limit: c?.credit_limit ?? f.credit_limit, credit_terms: c?.payment_terms || f.credit_terms }));
  };

  const save = async () => {
    const items = rows.filter((r) => r.item && r.item.trim()).map((r) => ({ item: r.item, hsn: r.hsn, qty: Number(r.qty) || 0, unit: r.unit || 'L', price: Number(r.price) || 0, gst: Number(r.gst) || 0 }));
    if (!items.length) return toast.error('Add at least one item');
    setSaving(true);
    try {
      const payload = { ...form, items, delivery_charge: Number(form.delivery_charge) || 0, delivery_gst: Number(form.delivery_gst) || 0, credit_limit: form.credit_limit === '' ? null : Number(form.credit_limit), customer_id: form.customer_id || null };
      const r = await api.post('/quotations', payload);
      toast.success(`Quotation ${r.data.quote_no} created`);
      setOpen(false);
      setRows([newRow()]);
      load();
      window.open(`${API}/quotations/${r.data.id}/pdf`, '_blank');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const delQuote = async (q) => {
    try { await api.delete(`/quotations/${q.id}`); toast.success(`Quotation ${q.quote_no} deleted`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  if (!list) return <Loading />;
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm" data-testid="new-quotation-button"><Plus className="h-4 w-4 mr-1" />New Quotation</Button></DialogTrigger>
          <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-3xl">
            <DialogHeader><DialogTitle>New Quotation / Provisional Invoice</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Billing Company</Label>
                  <Select value={form.billing_company_id} onValueChange={(v) => setForm({ ...form, billing_company_id: v })}>
                    <SelectTrigger data-testid="quote-company-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Customer (optional)</Label>
                  <Select value={form.customer_id || 'none'} onValueChange={(v) => (v === 'none' ? setForm({ ...form, customer_id: '' }) : pickCustomer(v))}>
                    <SelectTrigger data-testid="quote-customer-select"><SelectValue placeholder="Prospective / manual" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Prospective / manual</SelectItem>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Customer Name</Label><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="quote-customer-name" /></div>
                <div><Label>Customer GSTIN</Label><Input value={form.customer_gstin} onChange={(e) => setForm({ ...form, customer_gstin: e.target.value })} /></div>
                <div className="sm:col-span-2"><Label>Customer Address</Label><Input value={form.customer_address} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} /></div>
              </div>

              <div className="rounded-lg border border-border p-2">
                <div className="grid grid-cols-[20px_minmax(150px,1fr)_72px_58px_56px_72px_62px_64px_28px] gap-1 text-[11px] font-semibold text-muted-foreground px-1 pb-1">
                  <span>Sr</span><span>Item</span><span>HSN</span><span>Qty</span><span>Unit</span><span>Price</span><span>GST %</span><span className="text-right">Total</span><span />
                </div>
                {rows.map((r, i) => (
                  <div key={r._uid} className="grid grid-cols-[20px_minmax(150px,1fr)_72px_58px_56px_72px_62px_64px_28px] gap-1 items-center mb-1" data-testid={`quote-row-${i}`}>
                    <span className="text-xs text-muted-foreground pl-1">{i + 1}</span>
                    {r.product_id === 'custom' ? (
                      <div className="flex items-center gap-1">
                        <Input className="h-8" value={r.item} onChange={(e) => setRow(i, { item: e.target.value })} placeholder="Custom item name" data-testid={`quote-item-${i}`} />
                        <Button type="button" size="icon" variant="ghost" className="h-8 w-7 shrink-0" title="Pick from inventory" onClick={() => setRow(i, { product_id: '', item: '' })} data-testid={`quote-item-list-${i}`}><ListIcon className="h-3.5 w-3.5" /></Button>
                      </div>
                    ) : (
                      <Select value={r.product_id || undefined} onValueChange={(v) => pickProduct(i, v)}>
                        <SelectTrigger className="h-8" data-testid={`quote-item-${i}`}><SelectValue placeholder="Select item from inventory" /></SelectTrigger>
                        <SelectContent>
                          {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                          <SelectItem value="custom">Custom item…</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    <Input className="h-8" value={r.hsn} onChange={(e) => setRow(i, { hsn: e.target.value })} placeholder="HSN" />
                    <Input className="h-8" type="number" value={r.qty} onChange={(e) => setRow(i, { qty: e.target.value })} data-testid={`quote-qty-${i}`} />
                    <Select value={r.unit} onValueChange={(v) => setRow(i, { unit: v })}>
                      <SelectTrigger className="h-8" data-testid={`quote-unit-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input className="h-8" type="number" value={r.price} onChange={(e) => setRow(i, { price: e.target.value })} data-testid={`quote-price-${i}`} />
                    <Select value={String(r.gst)} onValueChange={(v) => setRow(i, { gst: v })}>
                      <SelectTrigger className="h-8" data-testid={`quote-gst-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{GST_RATES.map((g) => <SelectItem key={g} value={g}>{g}%</SelectItem>)}</SelectContent>
                    </Select>
                    <span className="text-xs text-right tabular pr-1">{fmtINR(lineTotal(r))}</span>
                    <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => rmRow(i)}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                  </div>
                ))}
                <Button type="button" size="sm" variant="outline" className="w-full mt-1" onClick={addRow} data-testid="quote-add-row"><Plus className="h-4 w-4 mr-1" />Add item</Button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div><Label>Delivery Charges</Label>
                  <div className="flex gap-1">
                    <Input type="number" value={form.delivery_charge} onChange={(e) => setForm({ ...form, delivery_charge: e.target.value })} placeholder="0" data-testid="quote-delivery" />
                    <Select value={form.delivery_gst} onValueChange={(v) => setForm({ ...form, delivery_gst: v })}>
                      <SelectTrigger className="w-[84px] shrink-0" data-testid="quote-delivery-gst"><SelectValue /></SelectTrigger>
                      <SelectContent>{GST_RATES.map((g) => <SelectItem key={g} value={g}>{g}% GST</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Credit Limit</Label><Input type="number" value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: e.target.value })} data-testid="quote-credit-limit" /></div>
                <div><Label>Credit Period</Label>
                  <Select value={form.credit_terms} onValueChange={(v) => setForm({ ...form, credit_terms: v })}>
                    <SelectTrigger data-testid="quote-credit-terms"><SelectValue /></SelectTrigger>
                    <SelectContent>{['Advance', 'Immediate', 'Net 7', 'Net 15', 'Net 30', 'Net 45', 'Net 60'].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Valid Until</Label><Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} /></div>
                <div className="sm:col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional note shown on the quotation" /></div>
              </div>
              <div className="flex justify-end gap-6 text-sm border-t border-border pt-2">
                <span className="text-muted-foreground">Sub Total: <b className="text-foreground">{fmtINR(subtotal)}</b></span>
                {gstTotal > 0 && <span className="text-muted-foreground">GST: <b className="text-foreground">{fmtINR(gstTotal)}</b></span>}
                <span className="text-muted-foreground">Grand Total: <b className="text-foreground" data-testid="quote-grand-total">{fmtINR(grand)}</b></span>
              </div>
            </div>
            <DialogFooter><Button onClick={save} disabled={saving} data-testid="save-quotation-button">{saving ? 'Creating...' : 'Create & Download PDF'}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm"><thead className="bg-muted/60"><tr className="text-left"><th className="p-3">Quotation</th><th className="p-3">Billed By</th><th className="p-3">Customer</th><th className="p-3">Grand Total</th><th className="p-3">Date</th><th className="p-3">Actions</th></tr></thead>
          <tbody>{list.map((q) => (
            <tr key={q.id} className="border-t border-border" data-testid={`quotation-${q.quote_no}`}>
              <td className="p-3 font-medium">{q.quote_no}</td><td className="p-3 text-xs">{q.seller_name}</td><td className="p-3">{q.customer_name || '-'}</td><td className="p-3 tabular">{fmtINR(q.grand_total)}</td><td className="p-3 text-muted-foreground">{fmtDate(q.date)}</td>
              <td className="p-3">
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="secondary" onClick={() => window.open(`${API}/quotations/${q.id}/pdf`, '_blank')} data-testid={`download-quote-${q.quote_no}`}><Download className="h-4 w-4 mr-1" />PDF</Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`delete-quote-${q.quote_no}`} aria-label="Delete quotation"><Trash2 className="h-4 w-4 text-destructive" /></Button></AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Delete quotation {q.quote_no}?</AlertDialogTitle><AlertDialogDescription>This permanently removes the quotation. The PDF will no longer be downloadable.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => delQuote(q)} data-testid={`confirm-delete-quote-${q.quote_no}`}>Delete</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </td>
            </tr>
          ))}</tbody></table>
        {list.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground flex flex-col items-center gap-2"><FileText className="h-6 w-6" />No quotations yet. Create one for either company.</div>}
      </div>
    </div>
  );
}

export default function AdminBilling() {
  const [invoices, setInvoices] = useState(null);
  const [payments, setPayments] = useState([]);
  const [payFor, setPayFor] = useState(null); // invoice being paid
  const [payForm, setPayForm] = useState({ amount: 0, method: 'UPI', date: new Date().toISOString().slice(0, 10) });
  const [saving, setSaving] = useState(false);
  const load = () => { api.get('/invoices').then((r) => setInvoices(r.data)); api.get('/payments').then((r) => setPayments(r.data)); };
  useEffect(() => { load(); }, []);
  if (!invoices) return <Loading />;
  const send = async (id) => { await api.post(`/invoices/${id}/send`); toast.success('Invoice sent'); };
  const pdf = (id) => window.open(`${API}/invoices/${id}/pdf`, '_blank');
  const openPay = (inv) => {
    setPayFor(inv);
    setPayForm({ amount: inv.balance ?? inv.amount, method: 'UPI', date: new Date().toISOString().slice(0, 10) });
  };
  const savePayment = async () => {
    if (!payForm.amount || payForm.amount <= 0) { toast.error('Enter a valid amount'); return; }
    if (!payForm.date) { toast.error('Pick the payment date'); return; }
    setSaving(true);
    try {
      const r = await api.post('/payments', { invoice_id: payFor.id, amount: Number(payForm.amount), method: payForm.method, date: payForm.date });
      toast.success(`Payment recorded — ${r.data.invoice_status} (balance ${fmtINR(r.data.balance)})`);
      setPayFor(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to record payment'); }
    setSaving(false);
  };
  return (
    <div className="space-y-4">
      <SectionTitle action={<Button variant="secondary" size="sm" onClick={() => window.open(`${API}/admin/reports/export?kind=invoices`, '_blank')}><Download className="h-4 w-4 mr-1" />Export</Button>}>Invoices & Payments</SectionTitle>
      <Tabs defaultValue="invoices">
        <TabsList><TabsTrigger value="invoices">Invoices</TabsTrigger><TabsTrigger value="payments">Payments</TabsTrigger><TabsTrigger value="quotations" data-testid="quotations-tab">Quotations</TabsTrigger><TabsTrigger value="bank" data-testid="bank-details-tab">Bank Details</TabsTrigger></TabsList>
        <TabsContent value="invoices" className="mt-3">
          <div className="rounded-xl border border-border bg-card overflow-x-auto"><table className="w-full text-sm"><thead className="bg-muted/60"><tr className="text-left"><th className="p-3">Invoice</th><th className="p-3">Customer</th><th className="p-3">Billed By</th><th className="p-3">Amount</th><th className="p-3">Balance</th><th className="p-3">Status</th><th className="p-3">Date</th><th className="p-3">Actions</th></tr></thead>
            <tbody>{invoices.map((i) => (
              <tr key={i.id} className="border-t border-border" data-testid={`admin-invoice-${i.invoice_no}`}><td className="p-3 font-medium">{i.invoice_no}</td><td className="p-3">{i.customer_name}</td><td className="p-3 text-xs">{i.seller?.name || 'M FUEL KART PVT LTD'}</td><td className="p-3 tabular">{fmtINR(i.amount)}</td><td className="p-3 tabular">{fmtINR(i.balance)}</td><td className="p-3"><PaymentBadge status={i.payment_status} /></td><td className="p-3 text-muted-foreground">{fmtDate(i.date)}</td>
                <td className="p-3 flex gap-1">
                  {(i.balance ?? 0) > 0 && <Button size="sm" variant="secondary" className="h-8 text-xs" onClick={() => openPay(i)} data-testid={`record-payment-${i.invoice_no}`}><IndianRupee className="h-3.5 w-3.5 mr-1" />Payment</Button>}
                  <Button size="icon" variant="ghost" onClick={() => pdf(i.id)}><Download className="h-4 w-4" /></Button><Button size="icon" variant="ghost" onClick={() => send(i.id)}><Send className="h-4 w-4" /></Button></td></tr>
            ))}</tbody></table></div>
        </TabsContent>
        <TabsContent value="payments" className="mt-3">
          <div className="rounded-xl border border-border bg-card overflow-x-auto"><table className="w-full text-sm"><thead className="bg-muted/60"><tr className="text-left"><th className="p-3">Invoice</th><th className="p-3">Customer</th><th className="p-3">Amount</th><th className="p-3">Method</th><th className="p-3">Date</th></tr></thead>
            <tbody>{payments.map((p) => <tr key={p.id} className="border-t border-border"><td className="p-3">{p.invoice_no}</td><td className="p-3">{p.customer_name}</td><td className="p-3 tabular">{fmtINR(p.amount)}</td><td className="p-3">{p.method}</td><td className="p-3 text-muted-foreground">{fmtDate(p.date)}</td></tr>)}</tbody></table></div>
        </TabsContent>
        <Dialog open={!!payFor} onOpenChange={(o) => !o && setPayFor(null)}>
          <DialogContent className="sm:max-w-md" data-testid="record-payment-dialog">
            <DialogHeader><DialogTitle>Record Payment — {payFor?.invoice_no}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">{payFor?.customer_name} · Invoice {fmtINR(payFor?.amount)} · Balance <b>{fmtINR(payFor?.balance)}</b></p>
              <div>
                <Label className="text-xs">Amount ₹</Label>
                <Input type="number" min="0" step="0.01" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} data-testid="payment-amount-input" />
              </div>
              <div>
                <Label className="text-xs">Method</Label>
                <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                  <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{['UPI', 'CASH', 'NEFT/RTGS', 'IMPS', 'CHEQUE', 'CARD', 'BANK TRANSFER', 'OTHER'].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Payment Date</Label>
                <Input type="date" value={payForm.date} onChange={(e) => setPayForm({ ...payForm, date: e.target.value })} data-testid="payment-date-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="secondary" onClick={() => setPayFor(null)} data-testid="payment-cancel-button">Cancel</Button>
              <Button onClick={savePayment} disabled={saving} data-testid="payment-save-button"><Save className="h-4 w-4 mr-1" />{saving ? 'Saving…' : 'Save Payment'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <TabsContent value="quotations" className="mt-3">
          <QuotationsTab />
        </TabsContent>
        <TabsContent value="bank" className="mt-3">
          <BankDetails />
        </TabsContent>
      </Tabs>
    </div>
  );
}
